import * as Yup from "yup";
import { TFunction } from "i18next";

import { convertLocaleValueToNumber } from "helpers/convertValueToLocale";

const NUMBER_WITH_COMMAS_REGEX = /^(?:\d{1,3}(?:\.\d{3})*|\d+)(?:,\d{1,2})?$/;
const NUMBER_REGEX = /^(-?\d+)$/;

import { CALCULATOR_TYPES, CALCULATOR_STAGES } from "constants/calculator";

export const getTabsConfig = ({
  t,
  isExpert,
}: {
  t: TFunction;
  isExpert: boolean;
}) => {
  if (isExpert) {
    return [
      {
        label: t("calculator_form.tabs.client"),
        value: "client",
        fieldList: [
          "client.hasSpouse",
          "client.taxableIncomeSpouseOne",
          "client.taxableIncomeSpouseTwo",
          "client.splitTaxableIncome",
        ],
      },
      {
        label: t("calculator_form.tabs.investObjects"),
        value: "investObjects",
        fieldList: ["apartments", "parkings"],
      },
      {
        label: t("calculator_form.tabs.investObjectParameters"),
        value: "investObjectParameters",
        fieldList: [
          "brokerFee",
          "propertyTax",
          "energyEfficiencyClass",
          "isSonderAfaForced",
          "stages[0].date",
          "stages[1].date",
          "stages[2].date",
          "stages[3].date",
          "managementCost",
          "managementCostGrowth",
          "managementCostGrowthDynamic",
          "maintenanceCostPerSqMeter",
          "maintenanceCostPerSqMeterGrowth",
          "maintenanceCostPerSqMeterGrowthDynamic",
          "downtimePercentage",
          "downtimePercentageDynamic",
          "priceGrowth.relationType",
          "priceGrowth.date",
        ],
      },
      {
        label: t("calculator_form.tabs.stages"),
        value: "stages",
      },
      {
        label: t("calculator_form.tabs.loans"),
        value: "loans",
        fieldList: [
          "client.loanYears",
          "client.capital",
          "loans[0].startRate",
          "loans[0].rate",
          "loans[1].startRate",
          "loans[1].rate",
        ],
      },
    ];
  }

  return [
    {
      label: t("calculator_form.tabs.client"),
      value: "client",
      fieldList: [
        "client.hasSpouse",
        "client.taxableIncomeSpouseOne",
        "client.taxableIncomeSpouseTwo",
        "client.splitTaxableIncome",
      ],
    },
    {
      label: t("calculator_form.tabs.investObjects"),
      value: "investObjects",
      fieldList: ["apartments", "parkings"],
    },
    {
      label: t("calculator_form.tabs.investObjectParameters"),
      value: "investObjectParameters",
      fieldList: [
        "brokerFee",
        "propertyTax",
        "energyEfficiencyClass",
        "isSonderAfaForced",
        "stages[0].date",
        "stages[1].date",
        "stages[2].date",
        "stages[3].date",
        "managementCost",
        "managementCostGrowth",
        "managementCostGrowthDynamic",
        "maintenanceCostPerSqMeter",
        "maintenanceCostPerSqMeterGrowth",
        "maintenanceCostPerSqMeterGrowthDynamic",
        "downtimePercentage",
        "downtimePercentageDynamic",
        "priceGrowth.relationType",
        "priceGrowth.date",
      ],
    },
    {
      label: t("calculator_form.tabs.loans"),
      value: "loans",
      fieldList: [
        "client.loanYears",
        "client.capital",
        "loans[0].startRate",
        "loans[0].rate",
        "loans[1].startRate",
        "loans[1].rate",
      ],
    },
  ];
};

export const getValidationSchema = ({
  calculatorType,
  t,
}: {
  calculatorType: string;
  t: TFunction;
}) => {
  if (calculatorType === CALCULATOR_TYPES.STANDARD) {
    return Yup.object().shape({
      client: Yup.object().shape({
        loanYears: Yup.string()
          .required(
            t("calculator_form.validation_errors.client.loan_period.required")
          )
          .matches(
            NUMBER_REGEX,
            t(
              "calculator_form.validation_errors.client.loan_period.invalid_format"
            )
          )
          .test(
            "greater-than-eleven",
            t("calculator_form.validation_errors.client.loan_period.too_short"),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue > 10;
            }
          )
          .test(
            "greater-than-eleven",
            t("calculator_form.validation_errors.client.loan_period.too_long"),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue <= 30;
            }
          ),

        taxableIncomeSpouseOne: Yup.string()
          .required(
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.required"
            )
          )
          .matches(
            NUMBER_WITH_COMMAS_REGEX,
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.invalid_format"
            )
          )
          .test(
            "greater-than-one",
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.must_be_positive"
            ),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue > 0;
            }
          ),
        taxableIncomeSpouseTwo: Yup.string().when("hasSpouse", {
          is: true,
          then: (schema) =>
            schema
              .required(
                t(
                  "calculator_form.validation_errors.client.taxable_income_spouse_two.required"
                )
              )
              .matches(
                NUMBER_WITH_COMMAS_REGEX,
                t(
                  "calculator_form.validation_errors.client.taxable_income_spouse_two.invalid_format"
                )
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
      apartments: Yup.array().of(
        Yup.object().shape({
          aptPrice: Yup.string()
            .required(
              t("calculator_form.validation_errors.apartment.price.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.price.invalid_format"
              )
            )
            .test(
              "greater-than-one",
              t(
                "calculator_form.validation_errors.apartment.price.must_be_positive"
              ),
              (value) => {
                if (!value) return false;
                const numValue = convertLocaleValueToNumber(value);
                return numValue > 0;
              }
            ),
          aptArea: Yup.string()
            .required(
              t("calculator_form.validation_errors.apartment.area.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.area.invalid_format"
              )
            )
            .test(
              "greater-than-one",
              t(
                "calculator_form.validation_errors.apartment.area.must_be_positive"
              ),
              (value) => {
                if (!value) return false;
                const numValue = convertLocaleValueToNumber(value);
                return numValue > 0;
              }
            ),
          // aptDinSize: Yup.string()
          //   .required(
          //     t(
          //       "calculator_form.validation_errors.apartment.area_din277.required"
          //     )
          //   )
          //   .matches(
          //     NUMBER_WITH_COMMAS_REGEX,
          //     t(
          //       "calculator_form.validation_errors.apartment.area_din277.invalid_format"
          //     )
          //   )
          //   .test(
          //     "greater-than-area",
          //     t(
          //       "calculator_form.validation_errors.apartment.area_din277.must_be_greater_than_area"
          //     ),
          //     function (value) {
          //       if (!value) return false;
          //       const dinSize = parseFloat(value);
          //       const area = parseFloat(this.parent.aptArea);
          //       return dinSize >= area;
          //     }
          //   ),
          aptRentPerMonth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.rent_per_month.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.rent_per_month.invalid_format"
              )
            ),
          aptDiscount: Yup.string()
            .required(
              t("calculator_form.validation_errors.apartment.discount.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.discount.invalid_format"
              )
            ),
          aptPriceGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.price_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.price_growth.invalid_format"
              )
            ),
          aptRentGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.rent_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.rent_growth.invalid_format"
              )
            ),
          aptLandShare: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.land_share.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.land_share.invalid_format"
              )
            ),
          aptFurniturePrice: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.furniture_price.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.furniture_price.invalid_format"
              )
            ),
        })
      ),
      parkings: Yup.array().of(
        Yup.object().shape({
          // parkingDinSize: Yup.string()
          //   .required(
          //     t(
          //       "calculator_form.validation_errors.parking.area_din277.required"
          //     )
          //   )
          //   .matches(
          //     NUMBER_WITH_COMMAS_REGEX,
          //     t(
          //       "calculator_form.validation_errors.parking.area_din277.invalid_format"
          //     )
          //   ),
          parkingPrice: Yup.string()
            .required(
              t("calculator_form.validation_errors.parking.price.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.price.invalid_format"
              )
            ),
          parkingDiscount: Yup.string()
            .required(
              t("calculator_form.validation_errors.parking.discount.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.discount.invalid_format"
              )
            ),
          parkingPriceGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.price_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.price_growth.invalid_format"
              )
            ),
          parkingRentPerMonth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.rent_per_month.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.rent_per_month.invalid_format"
              )
            ),
          parkingRentGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.rent_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.rent_growth.invalid_format"
              )
            ),
          parkingLandShare: Yup.string()
            .required(
              t("calculator_form.validation_errors.parking.land_share.required")
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.land_share.invalid_format"
              )
            ),
        })
      ),
      stages: Yup.array()
        .of(
          Yup.object().shape({
            date: Yup.string()
              .test(
                "required-for-non-start-stages",
                t("calculator_form.validation_errors.stages.date.required"),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.START_STAGE) {
                    return true;
                  }
                  return !!value;
                }
              )
              .test(
                "order-stage-date-validation-earlier",
                t(
                  "calculator_form.validation_errors.stages.purchase_date.too_early"
                ),
                function (value) {
                  if (!value) return true;

                  const stageType = this.parent?.type;

                  if (stageType === CALCULATOR_STAGES.PURCHASE_STAGE) {
                    const stageDate = new Date(value);
                    const minDate = new Date("2025-01-01");
                    return stageDate >= minDate;
                  }

                  return true;
                }
              )
              .test(
                "order-stage-date-validation-grater",
                t(
                  "calculator_form.validation_errors.stages.purchase_date.too_late"
                ),
                function (value) {
                  if (!value) return true;

                  const stageType = this.parent?.type;

                  if (stageType === CALCULATOR_STAGES.PURCHASE_STAGE) {
                    const stageDate = new Date(value);
                    const maxDate = new Date("2028-01-01");
                    return stageDate <= maxDate;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-finish-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.rental_income_min_date"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true;

                    const rentalIncomeDate = new Date(value);
                    const minDate = new Date("2025-01-01");

                    if (rentalIncomeDate < minDate) {
                      return false;
                    }

                    return true;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-finish-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.order_rental_income_date_finish_date_validation"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true;

                    const rentalIncomeDate = new Date(value);

                    const allStages =
                      this.from?.[this.from.length - 1]?.value?.stages;
                    if (allStages) {
                      const purchaseStage = allStages.find(
                        (stage: any) =>
                          stage.type === CALCULATOR_STAGES.FINISH_STAGE
                      );

                      if (purchaseStage?.date) {
                        const purchaseDate = new Date(purchaseStage.date);
                        return rentalIncomeDate >= purchaseDate;
                      }
                    }

                    return true;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-purchase-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.order_rental_income_date_purchase_date_validation"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true;

                    const rentalIncomeDate = new Date(value);

                    const allStages =
                      this.from?.[this.from.length - 1]?.value?.stages;
                    if (allStages) {
                      const purchaseStage = allStages.find(
                        (stage: any) =>
                          stage.type === CALCULATOR_STAGES.PURCHASE_STAGE
                      );

                      if (purchaseStage?.date) {
                        const purchaseDate = new Date(purchaseStage.date);
                        return rentalIncomeDate >= purchaseDate;
                      }
                    }

                    return true;
                  }

                  return true;
                }
              ),
          })
        )
        .test(
          "fee-sum-validation",
          t("calculator_form.validation_errors.stages.sum_must_be_100"),
          function (stages) {
            if (!stages || stages.length <= 4) return true;

            const totalFee = stages.reduce((sum: number, stage: any) => {
              const fee = convertLocaleValueToNumber(stage.fee || "0");
              return sum + fee;
            }, 0);

            return Math.round(totalFee * 100) / 100 === 100;
          }
        ),
      priceGrowth: Yup.object().shape({
        date: Yup.string()
          .test(
            "date-not-earlier-than-purchase",
            t("calculator_form.validation_errors.price_growth.date.too_early"),
            function (value) {
              const relationType = this.parent?.relationType?.value;

              if (relationType === "CUSTOM" && value) {
                const formValues = this.from?.[this.from.length - 1]?.value;
                const stages = formValues?.stages;

                if (stages && Array.isArray(stages)) {
                  const purchaseStage = stages.find(
                    (stage: any) =>
                      stage.type === CALCULATOR_STAGES.PURCHASE_STAGE
                  );

                  if (purchaseStage?.date) {
                    const customDate = new Date(value);
                    const purchaseDate = new Date(purchaseStage.date);
                    const customYearMonth =
                      customDate.getFullYear() * 12 + customDate.getMonth();
                    const purchaseYearMonth =
                      purchaseDate.getFullYear() * 12 + purchaseDate.getMonth();
                    return customYearMonth >= purchaseYearMonth;
                  }
                }
              }

              return true;
            }
          )
          .test(
            "date-not-later-than-finish",
            t("calculator_form.validation_errors.price_growth.date.too_late"),
            function (value) {
              const relationType = this.parent?.relationType?.value;

              if (relationType === "CUSTOM" && value) {
                const formValues = this.from?.[this.from.length - 1]?.value;
                const stages = formValues?.stages;

                if (stages && Array.isArray(stages)) {
                  const finishStage = stages.find(
                    (stage: any) =>
                      stage.type === CALCULATOR_STAGES.FINISH_STAGE
                  );

                  if (finishStage?.date) {
                    const customDate = new Date(value);
                    const finishDate = new Date(finishStage.date);
                    const customYearMonth =
                      customDate.getFullYear() * 12 + customDate.getMonth();
                    const finishYearMonth =
                      finishDate.getFullYear() * 12 + finishDate.getMonth();
                    return customYearMonth <= finishYearMonth;
                  }
                }
              }

              return true;
            }
          ),
      }),
      brokerFee: Yup.string()
        .required(t("calculator_form.validation_errors.broker_fee.required"))
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t("calculator_form.validation_errors.broker_fee.invalid_format")
        ),
      propertyTax: Yup.string()
        .required(t("calculator_form.validation_errors.property_tax.required"))
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t("calculator_form.validation_errors.property_tax.invalid_format")
        ),
      managementCost: Yup.string()
        .required(
          t("calculator_form.validation_errors.management_cost.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t("calculator_form.validation_errors.management_cost.invalid_format")
        ),
      managementCostGrowth: Yup.string()
        .required(
          t("calculator_form.validation_errors.management_cost_growth.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.management_cost_growth.invalid_format"
          )
        ),
      maintenanceCostPerSqMeter: Yup.string()
        .required(
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter.required"
          )
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter.invalid_format"
          )
        ),
      maintenanceCostPerSqMeterGrowth: Yup.string()
        .required(
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter_growth.required"
          )
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter_growth.invalid_format"
          )
        ),
      downtimePercentage: Yup.string()
        .required(
          t("calculator_form.validation_errors.downtime_percentage.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.downtime_percentage.invalid_format"
          )
        ),
    });
  }

  if (calculatorType === CALCULATOR_TYPES.LIMITED_PRO) {
    return Yup.object().shape({
      client: Yup.object().shape({
        taxableIncomeSpouseOne: Yup.string()
          .required(
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.required"
            )
          )
          .matches(
            NUMBER_WITH_COMMAS_REGEX,
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.invalid_format"
            )
          )
          .test(
            "greater-than-one",
            t(
              "calculator_form.validation_errors.client.taxable_income_spouse_one.must_be_positive"
            ),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue > 0;
            }
          ),
        taxableIncomeSpouseTwo: Yup.string().when("hasSpouse", {
          is: true,
          then: (schema) =>
            schema
              .required(
                t(
                  "calculator_form.validation_errors.client.taxable_income_spouse_two.required"
                )
              )
              .matches(
                NUMBER_WITH_COMMAS_REGEX,
                t(
                  "calculator_form.validation_errors.client.taxable_income_spouse_two.invalid_format"
                )
              ),
          otherwise: (schema) => schema.notRequired(),
        }),
      }),
      apartments: Yup.array().of(
        Yup.object().shape({
          aptRentPerMonth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.rent_per_month.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.rent_per_month.invalid_format"
              )
            ),
          aptPriceGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.price_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.price_growth.invalid_format"
              )
            ),
          aptRentGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.apartment.rent_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.apartment.rent_growth.invalid_format"
              )
            ),
        })
      ),
      parkings: Yup.array().of(
        Yup.object().shape({
          parkingPriceGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.price_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.price_growth.invalid_format"
              )
            ),
          parkingRentPerMonth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.rent_per_month.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.rent_per_month.invalid_format"
              )
            ),
          parkingRentGrowth: Yup.string()
            .required(
              t(
                "calculator_form.validation_errors.parking.rent_growth.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.parking.rent_growth.invalid_format"
              )
            ),
        })
      ),
      stages: Yup.array()
        .of(
          Yup.object().shape({
            date: Yup.string()
              .required(
                t("calculator_form.validation_errors.stages.date.required")
              )
              .test(
                "order-stage-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.purchase_stage_date_validation"
                ),
                function (value) {
                  if (!value) return true;

                  const stageType = this.parent?.type;

                  if (stageType === CALCULATOR_STAGES.PURCHASE_STAGE) {
                    const stageDate = new Date(value);
                    const minDate = new Date("2025-01-01");
                    return stageDate >= minDate;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-finish-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.rental_income_min_date"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true; // Если дата не указана, пропускаем валидацию

                    const rentalIncomeDate = new Date(value);
                    const minDate = new Date("2025-01-01");

                    if (rentalIncomeDate < minDate) {
                      return false;
                    }

                    return true;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-finish-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.order_rental_income_date_finish_date_validation"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true;

                    const rentalIncomeDate = new Date(value);

                    const allStages =
                      this.from?.[this.from.length - 1]?.value?.stages;
                    if (allStages) {
                      const purchaseStage = allStages.find(
                        (stage: any) =>
                          stage.type === CALCULATOR_STAGES.FINISH_STAGE
                      );

                      if (purchaseStage?.date) {
                        const purchaseDate = new Date(purchaseStage.date);
                        return rentalIncomeDate >= purchaseDate;
                      }
                    }

                    return true;
                  }

                  return true;
                }
              )
              .test(
                "order-rental-income-date-purchase-date-validation",
                t(
                  "calculator_form.validation_errors.stages.date.order_rental_income_date_purchase_date_validation"
                ),
                function (value) {
                  const stageType = this.parent?.type;
                  if (stageType === CALCULATOR_STAGES.RENTAL_INCOME_STAGE) {
                    if (!value) return true;

                    const rentalIncomeDate = new Date(value);

                    const allStages =
                      this.from?.[this.from.length - 1]?.value?.stages;
                    if (allStages) {
                      const purchaseStage = allStages.find(
                        (stage: any) =>
                          stage.type === CALCULATOR_STAGES.PURCHASE_STAGE
                      );

                      if (purchaseStage?.date) {
                        const purchaseDate = new Date(purchaseStage.date);
                        return rentalIncomeDate >= purchaseDate;
                      }
                    }

                    return true;
                  }

                  return true;
                }
              ),
          })
        )
        .test(
          "fee-sum-validation",
          t("calculator_form.validation_errors.stages.sum_must_be_100"),
          function (stages) {
            if (!stages || stages.length <= 4) return true;

            const totalFee = stages.reduce((sum: number, stage: any) => {
              const fee = convertLocaleValueToNumber(stage.fee || "0");
              return sum + fee;
            }, 0);

            return Math.round(totalFee * 100) / 100 === 100;
          }
        ),
      managementCost: Yup.string()
        .required(
          t("calculator_form.validation_errors.management_cost.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t("calculator_form.validation_errors.management_cost.invalid_format")
        ),
      managementCostGrowth: Yup.string()
        .required(
          t("calculator_form.validation_errors.management_cost_growth.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.management_cost_growth.invalid_format"
          )
        ),
      maintenanceCostPerSqMeter: Yup.string()
        .required(
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter.required"
          )
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter.invalid_format"
          )
        ),
      maintenanceCostPerSqMeterGrowth: Yup.string()
        .required(
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter_growth.required"
          )
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.maintenance_cost_per_sq_meter_growth.invalid_format"
          )
        ),
      downtimePercentage: Yup.string()
        .required(
          t("calculator_form.validation_errors.downtime_percentage.required")
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.downtime_percentage.invalid_format"
          )
        ),
    });
  }

  return Yup.object().shape({
    client: Yup.object().shape({
      taxableIncomeSpouseOne: Yup.string()
        .required(
          t(
            "calculator_form.validation_errors.client.taxable_income_spouse_one.required"
          )
        )
        .matches(
          NUMBER_WITH_COMMAS_REGEX,
          t(
            "calculator_form.validation_errors.client.taxable_income_spouse_one.invalid_format"
          )
        )
        .test(
          "greater-than-one",
          t(
            "calculator_form.validation_errors.client.taxable_income_spouse_one.must_be_positive"
          ),
          (value) => {
            if (!value) return false;
            const numValue = parseFloat(value);
            return numValue > 0;
          }
        ),
      taxableIncomeSpouseTwo: Yup.string().when("hasSpouse", {
        is: true,
        then: (schema) =>
          schema
            .required(
              t(
                "calculator_form.validation_errors.client.taxable_income_spouse_two.required"
              )
            )
            .matches(
              NUMBER_WITH_COMMAS_REGEX,
              t(
                "calculator_form.validation_errors.client.taxable_income_spouse_two.invalid_format"
              )
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    apartments: Yup.array().of(
      Yup.object().shape({
        aptPrice: Yup.string()
          .required(
            t("calculator_form.validation_errors.apartment.price.required")
          )
          .matches(
            NUMBER_WITH_COMMAS_REGEX,
            t(
              "calculator_form.validation_errors.apartment.price.invalid_format"
            )
          )
          .test(
            "greater-than-one",
            t(
              "calculator_form.validation_errors.apartment.price.must_be_positive"
            ),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue > 0;
            }
          ),
        aptArea: Yup.string()
          .required(
            t("calculator_form.validation_errors.apartment.area.required")
          )
          .matches(
            NUMBER_WITH_COMMAS_REGEX,
            t("calculator_form.validation_errors.apartment.area.invalid_format")
          )
          .test(
            "greater-than-one",
            t(
              "calculator_form.validation_errors.apartment.area.must_be_positive"
            ),
            (value) => {
              if (!value) return false;
              const numValue = convertLocaleValueToNumber(value);
              return numValue > 0;
            }
          ),
        aptRentPerMonth: Yup.string()
          .required(
            t(
              "calculator_form.validation_errors.apartment.rent_per_month.required"
            )
          )
          .matches(
            NUMBER_WITH_COMMAS_REGEX,
            t(
              "calculator_form.validation_errors.apartment.rent_per_month.invalid_format"
            )
          ),
      })
    ),
    brokerFee: Yup.string()
      .required(t("calculator_form.validation_errors.broker_fee.required"))
      .matches(
        NUMBER_WITH_COMMAS_REGEX,
        t("calculator_form.validation_errors.broker_fee.invalid_format")
      ),
    propertyTax: Yup.string()
      .required(t("calculator_form.validation_errors.property_tax.required"))
      .matches(
        NUMBER_WITH_COMMAS_REGEX,
        t("calculator_form.validation_errors.property_tax.invalid_format")
      ),
  });
};
